import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import AuthHeader from '@/common/components/AuthHeader/AuthHeader';
import Loader from '@/common/components/Loader/Loader';
import { UserLogin, SetPassword } from '@/common/containers/Forms';
import { routes } from '@/app/routing';
import { useAuth } from '@/app/providers/AuthProvider';
import {
  useCompleteNewPasswordMutation,
  useLoginMutation,
} from '@/entities/user';
import { Notification } from '@/common/utils';
import loginStyles from '@/modules/login/login.styles';
import { getAuthorizedRedirectPath } from '@/app/routing/auth-redirect';
import { getInviteCredentials } from '@/app/routing/invite-params';

const LoginModule = ({ isAdmin = false }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const loginMutation = useLoginMutation();
  const completeNewPasswordMutation = useCompleteNewPasswordMutation();
  const { inviteEmail, invitePassword, inviteFirstName, inviteLastName } =
    useMemo(
      () => getInviteCredentials(location.search, location.hash),
      [location.hash, location.search]
    );
  const hasInviteCredentials = !!inviteEmail && !!invitePassword;
  const isInviteRedirectPending = !isAdmin && hasInviteCredentials;
  const getSetupAccountPath = useCallback(
    (email, password, firstName = '', lastName = '') => {
      const inviteParams = new URLSearchParams({
        m: email,
        fn: firstName.trim(),
        ln: lastName.trim(),
        p: password,
      });

      return `${routes.setupAccount}?${inviteParams.toString()}`;
    },
    []
  );

  const saveUser = useCallback(() => {
    return refreshUser().then((nextUser) => {
      history.push(getAuthorizedRedirectPath(nextUser));
    });
  }, [history, refreshUser]);

  useEffect(() => {
    if (isAdmin || !hasInviteCredentials) {
      return;
    }

    history.replace(
      getSetupAccountPath(
        inviteEmail,
        invitePassword,
        inviteFirstName,
        inviteLastName
      )
    );
  }, [
    getSetupAccountPath,
    hasInviteCredentials,
    history,
    inviteEmail,
    inviteFirstName,
    inviteLastName,
    invitePassword,
    isAdmin,
  ]);

  const onSubmitLogin = useCallback(
    async ({ email, password }) => {
      try {
        const authUser = await loginMutation.mutateAsync({ email, password });

        if (
          authUser?.nextStep?.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          if (isAdmin) {
            setUser(authUser);
            return;
          }

          history.push(getSetupAccountPath(email, password));
          return;
        }

        setUser(null);
        await saveUser();
      } catch (error) {
        Notification.error(error?.message);
        throw error;
      }
    },
    [getSetupAccountPath, history, isAdmin, loginMutation, saveUser]
  );

  const onSetPasswordSubmit = useCallback(
    async ({ password }) => {
      try {
        await completeNewPasswordMutation.mutateAsync({
          password,
        });
        setUser(null);
        await saveUser();
      } catch (error) {
        Notification.error(error?.message);
        throw error;
      }
    },
    [completeNewPasswordMutation, saveUser]
  );

  if (isInviteRedirectPending) {
    return (
      <Box component="section" sx={loginStyles.container}>
        <AuthHeader />
        <Box sx={loginStyles.formWrapper}>
          <Typography variant="h2" textAlign="center" mb={10}>
            {t('Checking your access link...')}
          </Typography>
          <Loader />
        </Box>
      </Box>
    );
  }

  return (
    <Box component="section" sx={loginStyles.container}>
      <AuthHeader />
      <Box sx={loginStyles.formWrapper}>
        <Typography variant="h2" textAlign="center" mb={10}>
          {!user
            ? isAdmin
              ? t('Admin Panel Login')
              : t('Log in')
            : t('Set the password')}
        </Typography>
        {!user ? (
          <Box sx={loginStyles.formWrap}>
            <UserLogin
              onSubmit={onSubmitLogin}
              isPending={loginMutation.isPending}
            >
              <Button
                variant="text"
                size="small"
                color="success"
                component={RouterLink}
                to={routes.resetPassword}
                sx={{ width: 'fit-content', mt: 2 }}
              >
                {t('Forgot password?')}
              </Button>
            </UserLogin>
          </Box>
        ) : (
          <Box sx={loginStyles.formWrap}>
            <SetPassword
              onSubmit={onSetPasswordSubmit}
              withPolicy={!isAdmin}
              isPending={completeNewPasswordMutation.isPending}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LoginModule;
